/**
 * An animation whose children are updated in parallel.
 * This is a TypeScript port of the C++ AnimationsInParallel class from
 * tabanim/public/animation/AnimationsInParallel.h
 *
 * Use this for multiple animations to run simultaneously at the same pace.
 */

import { AnimationGroup } from './AnimationGroup';
import { Animation } from './BaseAnimation';

/**
 * This corresponds to C++ IPaneState that can be animated by PaneStateAnimations.
 * The actual implementation is provided by the consumer of this animation library.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IPaneState {
  // Marker interface - implementations will have specific properties
  // This maintains type safety while allowing flexibility
}

/**
 * Callback function invoked after all children have been updated.
 * Corresponds to C++ AnimationsInParallelPostUpdatesFunc: std::function<void(void)>
 * TODO : implement the Animation_Common.h for placing the callback function
 */
export type PostUpdatesFunc = () => void;

/**
 * Callback function for pane state animations.
 * TODO : implement the Animation_Common.h for placing the callback function
 */
export type PaneStatePostUpdateFunc = (state: IPaneState) => void;

export class AnimationsInParallel extends AnimationGroup {
  /**
   * Callback invoked after all children updates complete.
   * Corresponds to C++ m_postUpdatesFunc
   */
  protected postUpdatesFunc?: PostUpdatesFunc;

  /**
   * Callback for pane state animations.
   * Corresponds to C++ m_paneStateAnimationPostUpdateFunc
   */
  protected paneStatePostUpdateFunc?: PaneStatePostUpdateFunc;

  /**
   * State object passed to pane state callback.
   * Corresponds to C++ m_stateForUpdateFunc (IPaneStatePtr)
   */
  protected paneState?: IPaneState;

  constructor();
  constructor(postUpdatesFunc: PostUpdatesFunc);
  constructor(paneState: IPaneState, paneStatePostUpdateFunc: PaneStatePostUpdateFunc);

  constructor(arg1?: PostUpdatesFunc | IPaneState, arg2?: PaneStatePostUpdateFunc) {
    super();

    if (typeof arg1 === 'function' && arg2 === undefined) {
      this.postUpdatesFunc = arg1 as PostUpdatesFunc;
    } else if (arg1 !== undefined && typeof arg2 === 'function') {
      this.paneState = arg1;
      this.paneStatePostUpdateFunc = arg2;
    }
    // Default constructor: all callbacks remain undefined
  }

  public addChild(child: Animation | null): void {
    if (child) {
      this.children.push(child);
    }
  }

  public removeChild(child: Animation | null): void {
    if (child) {
      const index = this.children.indexOf(child);
      if (index >= 0) {
        this.children.splice(index, 1); // Remove exactly one element
      }
    }
  }

  public update(): void {
    // Update all children with the same fraction
    for (const child of this.children) {
      child.setFraction(this.getFraction());
    }

    if (this.postUpdatesFunc) {
      this.postUpdatesFunc();
    }

    if (this.paneStatePostUpdateFunc && this.paneState) {
      this.paneStatePostUpdateFunc(this.paneState);
    }
  }
}
